import { Row, Col, message, Table, Form, Input, Typography, Modal, Button } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import React, { useCallback, useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { AppFilterButton, AppFilter } from "components/AppFilter";
import { AppAvailableOrdersConfigFilter } from "../../components/AppFilter/AppAvailableOrdersConfigFilter/AppAvailableOrdersConfigFilter";
import { getAvailableOrdersConfig, updateAvailableOrdersConfig } from "../../store/ppd/ppdSlice";
import { IAvailableOrder } from "../../common/interface/IAvailableOrders";
import { getDarkStores } from "../../store/admin-slice/adminServicesSlice";
import { ALLOWED_SLOT_IDS, getSlotValue } from "../../common/constants";
import { EditOutlined } from "@ant-design/icons";

type PartialAvailableOrderType = Partial<IAvailableOrder> & { key: string };

const MESSAGE_KEY = "available_orders_config";

const StyledCautionMessage = styled.div`
  color: #ff4d4f;
  font-weight: 500;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  background-color: #fff2f0;
  border-left: 4px solid #ff4d4f;
  border-radius: 2px;
`;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
    editing: boolean;
    dataIndex: string;
    title: any;
    record: PartialAvailableOrderType;
    children: React.ReactNode;
}

const headerKeys: { [key: string]: string } = {
    slotId: "Slot ID",
    slot: "Slot",
    entityType: "Entity Type",
    entityId: "Store ID",
    availableCount: "Available Count",
    remainingCount: "Remaining Count",
    actions: "Actions",
};

const AvailableOrdersConfigPage = () => {
    useDocumentTitle("Order Slots Cap Config");
    const dispatch = useAppDispatch();
    const [columns, setColumns] = useState<ColumnsType<PartialAvailableOrderType>>();
    const [rows, setRows] = useState<PartialAvailableOrderType[]>();
    const { availableOrdersConfig, status, error } = useAppSelector((state) => state.ppd);
    const [filterState, setFilterState] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [filterCount, setFilterCount] = useState(0);
    const [form] = Form.useForm();
    const [editingKey, setEditingKey] = useState("");
    const [warningModalVisible, setWarningModalVisible] = useState(false);
    const [pendingUpdate, setPendingUpdate] = useState<{
        record: PartialAvailableOrderType;
        newAvailableCount: number;
        newRemainingCount: number;
    } | null>(null);
    const previousStatusRef = useRef<string>("");

    const getAvailableOrdersConfigData = useCallback(
        (filter = "") => {
            const params = Object.fromEntries(new URLSearchParams(filter));

            dispatch(getAvailableOrdersConfig({
                storeId: params.storeId,
                slotId: params.slotId
            }));
        },
        [dispatch]
    );

    const isEditing = useCallback(
        (record: PartialAvailableOrderType) => record.key === editingKey,
        [editingKey]
    );

    const edit = useCallback(
        (record: PartialAvailableOrderType & { key: React.Key }) => {
            form.setFieldsValue({ ...record });
            setEditingKey(record.key as string);
        },
        [form]
    );

    const performUpdate = useCallback(
        (record: PartialAvailableOrderType, updatedAvailableCount: number) => {
            // Validate required fields before dispatch
            if (!record.entityId || !record.slotId) {
                console.error("Missing required fields for update:", { entityId: record.entityId, slotId: record.slotId });
                return;
            }

            dispatch(updateAvailableOrdersConfig({
                storeId: record.entityId,
                slotId: record.slotId,
                availableCount: updatedAvailableCount,
            }));

            setEditingKey("");
            form.resetFields();
        },
        [form, dispatch]
    );

    const save = useCallback(
        async (record: PartialAvailableOrderType) => {
            try {
                const row = await form.validateFields();
                const newAvailableCount = row.availableCount;
                const currentAvailableCount = record.availableCount || 0;
                const currentRemainingCount = record.remainingCount || 0;

                // Calculate the change in available count
                const difference = currentAvailableCount - newAvailableCount;
                // Calculate what the new remaining count will be
                const newRemainingCount = currentRemainingCount - difference;

                // Check if remaining count would be 0 or negative, or if availability is being set to 0
                if (newRemainingCount <= 0 || newAvailableCount === 0) {
                    setPendingUpdate({
                        record,
                        newAvailableCount,
                        newRemainingCount,
                    });
                    setWarningModalVisible(true);
                } else {
                    // No warning needed, proceed with update
                    performUpdate(record, newAvailableCount);
                }
            } catch (errInfo) {
                console.log("Validate Failed:", errInfo);
            }
        },
        [form, performUpdate]
    );

    const cancel = useCallback(() => {
        setEditingKey("");
        form.resetFields();
    }, [form]);

    // 1. Fetch stores on component mount
    useEffect(() => {
        dispatch(getDarkStores());
    }, [dispatch]);

    // 2. Watch searchParams and fetch data
    useEffect(() => {
        const filter = searchParams.toString();
        const params = Object.fromEntries(new URLSearchParams(filter));

        setFilterCount(Object.keys(params).length);

        getAvailableOrdersConfigData(filter);
    }, [searchParams, getAvailableOrdersConfigData]);

    // 3. Handle loading, success, error states
    useEffect(() => {
        if (status === "loading") {
            message.loading({ content: "Loading available orders config", key: MESSAGE_KEY });
            // Clear rows when loading starts to show loading spinner
            setRows(undefined);
        } else if (status === "saving") {
            message.loading({ content: "Saving available count", key: MESSAGE_KEY });
        } else if (status === "success") {
            message.success({ content: "Updated successfully", key: MESSAGE_KEY });
            // Only refresh if we just completed a save operation (transitioning from "saving" to "success")
            if (previousStatusRef.current === "saving") {
                const timer = setTimeout(() => {
                    const filter = searchParams.toString();
                    getAvailableOrdersConfigData(filter);
                }, 500);
                return () => clearTimeout(timer);
            }
        } else if (status === "error") {
            message.error({ content: error, key: MESSAGE_KEY });
        }

        // Track the previous status
        previousStatusRef.current = status;

        return () => {
            message.destroy();
        };
    }, [status, error, searchParams, getAvailableOrdersConfigData]);

    // 4. Process and display data
    useEffect(() => {
        const isLoadingOrSaving = status === "loading" || status === "saving";

        // Skip updating rows while loading to show loading spinner
        if (isLoadingOrSaving) return;

        if (!availableOrdersConfig || !Array.isArray(availableOrdersConfig)) return;

        const availableOrdersConfigData = Array.isArray(availableOrdersConfig) ? availableOrdersConfig : [];

        // Pre-filter data by allowed slot IDs
        const preFilteredOrders = availableOrdersConfigData.filter(
            (order): order is IAvailableOrder =>
                order && typeof order.slotId === "number" && ALLOWED_SLOT_IDS.includes(order.slotId)
        );

        const data: PartialAvailableOrderType[] = preFilteredOrders.map((order, i) => ({
            ...order,
            key: `availableOrdersConfig-${i}`,
        }));

        const tableHeaderKeys = Object.keys(headerKeys).filter((key) => key !== "actions") as (keyof typeof headerKeys)[];

        const tableHeaders: ColumnsType<PartialAvailableOrderType> = [
            ...tableHeaderKeys.map((headerKey: keyof typeof headerKeys) => ({
                key: headerKey,
                title: headerKeys[headerKey],
                dataIndex: headerKey,
                width: getHeaderWeight(String(headerKey)),
                editable: headerKey === "availableCount",
                render: (text: string | number | undefined, record: PartialAvailableOrderType) => {
                    if (headerKey === "slot") {
                        return <>{getSlotValue(record.slotId)}</>;
                    }

                    return <>{text ?? "N/A"}</>;
                },
            })),
            {
                key: "actions",
                title: headerKeys["actions"],
                width: 120,
                render: (_: undefined, record: PartialAvailableOrderType) => {
                    const editable = isEditing(record);
                    return editable ? (
                        <Row align="middle" style={{ display: "flex", flexWrap: "wrap" }}>
                            <Col style={{ marginRight: ".5rem" }}>
                                <Typography.Link
                                    onClick={() => save(record)}
                                    disabled={isLoadingOrSaving}
                                    style={{ marginRight: 8 }}
                                >
                                    Save
                                </Typography.Link>
                            </Col>
                            <Col>
                                <Typography.Link
                                    onClick={cancel}
                                    style={{ marginRight: 8 }}
                                    type="secondary"
                                    disabled={isLoadingOrSaving}
                                >
                                    Cancel
                                </Typography.Link>
                            </Col>
                        </Row>
                    ) : (
                        <Typography.Link onClick={() => edit(record)}>
                            <EditOutlined style={{ marginRight: 8 }} />
                            Edit
                        </Typography.Link>
                    );
                },
            },
        ];

        // Add onCell for editable columns
        const mergedColumns = tableHeaders.map((col) => {
            if (col.key !== "availableCount" || !("dataIndex" in col)) {
                return col;
            }
            return {
                ...col,
                onCell: (record: PartialAvailableOrderType) => ({
                    record,
                    dataIndex: col.dataIndex as string,
                    title: col.title,
                    editing: isEditing(record),
                }),
            };
        }) as ColumnsType<PartialAvailableOrderType>;

        setColumns(mergedColumns);
        setRows(data);
    }, [availableOrdersConfig, editingKey, isEditing, edit, save, cancel, status]);

    const EditableCell: React.FC<EditableCellProps> = ({
        editing,
        dataIndex,
        title,
        record,
        children,
        ...restProps
    }) => {
        return (
            <td {...restProps}>
                {editing ? (
                    <Form.Item
                        name={dataIndex}
                        style={{ margin: 0 }}
                        rules={[
                            {
                                required: true,
                                message: `Please input ${title}!`,
                            },
                            {
                                message: "Value cannot be negative",
                                validator: (_, value) => {
                                    if (value === "" || value === undefined) {
                                        return Promise.reject("Please enter a value");
                                    }
                                    const numberValue = parseInt(value, 10);
                                    if (isNaN(numberValue)) {
                                        return Promise.reject("Please enter a number");
                                    }
                                    if (numberValue < 0) {
                                        return Promise.reject("Value cannot be negative");
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input
                            type="number"
                            min="0"
                            step="1"
                        />
                    </Form.Item>
                ) : (
                    children
                )}
            </td>
        );
    };

    function getHeaderWeight(key: string): number | undefined {
        const headerWeights: Record<string, number> = {
            slotid: 100,
            slot: 120,
            entitytype: 120,
            entityid: 120,
            availablecount: 140,
            remainingcount: 140,
            actions: 120,
        };

        return headerWeights[key.toLowerCase()];
    }

    return (
        <>
            <Row>
                <Col>
                    <AppPageHeader title="Order Slots Cap Config" />
                </Col>
            </Row>
            <Row>
                <AppActionStrip>
                    <div className="action-strip__item">
                        <AppFilterButton
                            active={filterState}
                            onClick={() => setFilterState(!filterState)}
                            filterCount={filterCount}
                        />
                    </div>
                </AppActionStrip>
            </Row>
            <Row style={{ marginBottom: "1rem" }}>
                <AppFilter expanded={filterState}>
                    <AppAvailableOrdersConfigFilter />
                </AppFilter>
            </Row>
            <Row>
                <Col xs={24}>
                    <StyledCautionMessage>
                        <strong>Caution:</strong> Updating the config will apply the new order cap to all future orders and override any caps already set from tomorrow onward. Today's orders will remain unaffected.
                    </StyledCautionMessage>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <Form form={form} component={false}>
                        <Table
                            columns={columns}
                            dataSource={rows}
                            scroll={{ x: "100vh", y: "65vh" }}
                            size="small"
                            pagination={false}
                            loading={status === "loading" || status === "saving"}
                            bordered
                            locale={{
                                emptyText: status !== "loading" && status !== "saving" ? "No available orders config to show." : undefined
                            }}
                            components={{
                                body: {
                                    cell: EditableCell,
                                },
                            }}
                        />
                    </Form>
                </Col>
            </Row>

            <Modal
                title="Warning"
                open={warningModalVisible}
                onCancel={() => {
                    setWarningModalVisible(false);
                    setPendingUpdate(null);
                }}
                footer={
                    <Row justify="space-between" style={{ marginTop: "1.5rem" }}>
                        <Col>
                            <Button
                                onClick={() => {
                                    setWarningModalVisible(false);
                                    setPendingUpdate(null);
                                }}
                                style={{ minWidth: "100px" }}
                            >
                                Cancel
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                onClick={() => {
                                    if (pendingUpdate) {
                                        performUpdate(pendingUpdate.record, pendingUpdate.newAvailableCount);
                                        setWarningModalVisible(false);
                                        setPendingUpdate(null);
                                    }
                                }}
                                style={{ minWidth: "100px" }}
                            >
                                OK
                            </Button>
                        </Col>
                    </Row>
                }
            >
                {pendingUpdate && (
                    <div>
                        <p>
                            Updating availability to <strong>{pendingUpdate.newAvailableCount}</strong> will cause the remaining count to be{" "}
                            <strong>{pendingUpdate.newRemainingCount}</strong>.
                        </p>
                        <p>Do you want to proceed?</p>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default AvailableOrdersConfigPage;
